import React, { useState, useEffect, useRef } from 'react';
import { CRITERIA, COUNTRY_CODES } from '../constants';
import ReactCountryFlag from 'react-country-flag';

const Voting = ({ activeContest, updateScore, allScores, user, reactions, lastChanged, activeContestantId, activeOrder }) => {
    const [expandedContestant, setExpandedContestant] = useState(null);
    const [savingIds, setSavingIds] = useState(new Set());
    const [flashId, setFlashId] = useState(null);
    const [flashClass, setFlashClass] = useState('');
    const flashTimerRef = useRef(null);
    const activeRowRef = useRef(null);

    // My scores are derived directly from allScores (single source of truth) —
    // no local tempScores buffer that can be wiped by snapshot updates.
    const myScores = React.useMemo(() => {
        const out = {};
        if (!user || !activeContest?.contestants) return out;
        activeContest.contestants.forEach(c => {
            const mine = allScores?.[c.id]?.[user.id];
            if (mine) {
                const entry = {};
                CRITERIA.forEach(cr => {
                    if (mine[cr.id] !== undefined) entry[cr.id] = mine[cr.id];
                });
                if (mine.overall !== undefined) entry.overall = mine.overall;
                if (Object.keys(entry).length) out[c.id] = entry;
            }
        });
        return out;
    }, [allScores, user, activeContest?.contestants]);

    // Flash animation when other users update scores
    useEffect(() => {
        if (!lastChanged || !user) return;
        const { contestantId, voterId, overall } = lastChanged;
        if (voterId && voterId === user.id) return; // skip own
        const scores = allScores?.[contestantId];
        if (!scores) return;
        let cls = 'flash-update';
        if (typeof overall === 'number') {
            if (overall >= 8) cls = 'flash-high';
            else if (overall >= 5) cls = 'flash-mid';
            else cls = 'flash-low';
        }
        setFlashId(contestantId);
        setFlashClass(cls);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => { setFlashId(null); setFlashClass(''); }, 1100);
    }, [lastChanged, allScores, user]);
    useEffect(() => {
        if (activeContestantId && activeRowRef.current) {
            activeRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [activeContestantId]);

    const handleScoreSelect = async (contestantId, criterionId, value) => {
        setSavingIds(prev => {
            const next = new Set(prev);
            next.add(contestantId);
            return next;
        });
        try {
            await updateScore(contestantId, criterionId, value);
        } finally {
            setSavingIds(prev => {
                const next = new Set(prev);
                next.delete(contestantId);
                return next;
            });
        }
    };

    const handleToggleExpand = (contestantId) => {
        setExpandedContestant(prev => prev === contestantId ? null : contestantId);
    };

    // Floating emoji reactions for a contestant — hide reactions from prior contests
    // (contestantId is reused across SF1/SF2/Final, so filter by Final start cutoff)
    const REACTION_CUTOFF_MS = Date.parse('2026-05-16T18:00:00Z');
    const getContestantReactions = (contestantId) => {
        if (!reactions) return [];
        return reactions.filter(r => {
            if (r.contestantId == null || String(r.contestantId) !== String(contestantId)) return false;
            const ts = r.timestamp?.toMillis?.() ?? r.timestamp?.toDate?.()?.getTime?.() ?? 0;
            return ts >= REACTION_CUTOFF_MS;
        });
    };

    const scoreColor = (val) => {
        if (val >= 8) return 'var(--cyan)';
        if (val >= 5) return 'var(--gold)';
        return 'var(--text3)';
    };

    // Compute dynamic rank map: average overall score across all room members → sort desc → competition ranking
    const rankMap = React.useMemo(() => {
        if (!activeContest?.contestants || !allScores) return {};
        const avgs = activeContest.contestants.map(c => {
            const scores = allScores[c.id];
            if (!scores) return { id: c.id, avg: null };
            const vals = Object.values(scores)
                .map(s => parseFloat(s.overall))
                .filter(n => !isNaN(n));
            return { id: c.id, avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null };
        });
        // Sort desc by avg (nulls last)
        const sorted = [...avgs].sort((a, b) => {
            if (a.avg === null && b.avg === null) return 0;
            if (a.avg === null) return 1;
            if (b.avg === null) return -1;
            return b.avg - a.avg;
        });
        // Standard competition ranking (ties share rank, next rank skips)
        const out = {};
        let rank = 1;
        for (let i = 0; i < sorted.length; i++) {
            if (sorted[i].avg === null) break; // no score → no rank
            if (i > 0 && sorted[i].avg === sorted[i - 1].avg) {
                out[String(sorted[i].id)] = out[String(sorted[i - 1].id)];
            } else {
                out[String(sorted[i].id)] = rank;
            }
            rank = i + 2;
        }
        return out;
    }, [allScores, activeContest?.contestants]);

    if (!activeContest?.contestants?.length) {
        return <div className="p-4 text-center text-sm" style={{ color: 'var(--text3)' }}>No contestants available.</div>;
    }

    // Eurovision contests use runningOrder. If contest looks like a euro2026 contest but runningOrder
    // hasn't arrived yet, show a skeleton instead of rendering alphabetical and snapping later.
    const expectsRunningOrder = String(activeContest.id || '').startsWith('euro2026');
    const runningOrderReady = (activeContest.runningOrder?.length || 0) > 0;
    if (expectsRunningOrder && !runningOrderReady) {
        return (
            <div className="max-w-xl mx-auto pb-4">
                {Array.from({ length: activeContest.contestants.length }).map((_, i) => (
                    <div key={i} className="contestant-row" style={{ opacity: 0.4 }}>
                        <div className="flex items-center gap-2.5 min-w-0 py-3 pl-3 flex-1">
                            <span className="font-mono text-xs w-5 text-right" style={{ color: 'var(--text3)' }}>—</span>
                            <div className="w-5 h-4" style={{ background: 'var(--surface-hover)' }} />
                            <div className="h-3 flex-1" style={{ background: 'var(--surface-hover)' }} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Build position map from runningOrder if present, else fall back to id sort
    const posMap = {};
    if (activeContest.runningOrder?.length) {
        activeContest.runningOrder.forEach(entry => {
            posMap[String(entry.contestantId)] = entry.order;
        });
    }
    const hasRunningOrder = Object.keys(posMap).length > 0;
    const sortedContestants = activeContest.contestants
        .slice()
        .sort((a, b) => {
            if (hasRunningOrder) {
                const pa = posMap[String(a.id)] ?? 999;
                const pb = posMap[String(b.id)] ?? 999;
                return pa - pb;
            }
            return (a.order || parseInt(a.id)) - (b.order || parseInt(b.id));
        });

    return (
        <div className="max-w-xl mx-auto pb-4">
            <div>
                {sortedContestants
                    .map((contestant) => {
                        const isExpanded = expandedContestant === contestant.id;
                        const hasScore = myScores[contestant.id]?.overall;
                        const isSaving = savingIds.has(contestant.id);
                        const isFlashing = flashId != null && String(flashId) === String(contestant.id);
                        const contestantReactions = getContestantReactions(contestant.id);
                        const isAdmin = !!user?.isAdmin;
                        const myPos = posMap[String(contestant.id)] ?? null;
                        const isLocked = !isAdmin && hasRunningOrder && myPos != null && (activeOrder == null || myPos > activeOrder);

                        return (
                            <div key={contestant.id} className="relative">
                                {/* Contestant row */}
                                <div
                                    ref={String(contestant.id) === String(activeContestantId) ? activeRowRef : null}
                                    className={`contestant-row ${hasScore ? 'voted' : ''} ${isFlashing ? flashClass : ''} ${isLocked ? 'locked-row' : ''}`}
                                    onClick={() => { if (!isLocked) handleToggleExpand(contestant.id); }}
                                    style={{
                                        borderLeft: isExpanded ? '4px solid var(--pink)' : undefined,
                                        opacity: isLocked ? 0.45 : 1,
                                        cursor: isLocked ? 'not-allowed' : 'pointer',
                                        ...(String(contestant.id) === String(activeContestantId) ? { borderLeft: '3px solid var(--pink)', background: 'rgba(255, 45, 135, 0.05)' } : {}),
                                    }}
                                >
                                    <div className="flex items-center gap-2.5 min-w-0 py-3 pl-3 flex-1">
                                        <span
                                            className="font-mono text-xs w-5 text-right flex-shrink-0"
                                            style={{ color: 'var(--text3)', fontFamily: '"JetBrains Mono", monospace' }}
                                        >
                                            {rankMap[String(contestant.id)] != null ? `#${rankMap[String(contestant.id)]}` : '-'}
                                        </span>
                                        {contestant.country && COUNTRY_CODES[contestant.country] && (
                                            <ReactCountryFlag
                                                countryCode={COUNTRY_CODES[contestant.country]}
                                                svg
                                                style={{ width: '1.3em', height: '1.3em' }}
                                                title={contestant.country}
                                            />
                                        )}
                                        <div className="min-w-0">
                                            <span
                                                className="font-display text-sm truncate block"
                                                style={{ fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase' }}
                                            >
                                                {contestant.country || contestant.name}
                                            </span>
                                            {contestant.country && contestant.name !== contestant.country && (
                                                <span className="text-[11px] truncate block" style={{ color: 'var(--text2)' }}>
                                                    {contestant.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 pr-3 flex-shrink-0">
                                        {hasScore && !isLocked && (
                                            <span
                                                className="font-mono text-sm font-bold tabular-nums"
                                                style={{
                                                    fontFamily: '"JetBrains Mono", monospace',
                                                    color: isSaving ? 'var(--gold)' : scoreColor(myScores[contestant.id].overall),
                                                }}
                                            >
                                                {myScores[contestant.id].overall.toFixed(1)}
                                            </span>
                                        )}
                                        {isLocked ? (
                                            <svg
                                                className="w-4 h-4"
                                                style={{ color: 'var(--text3)' }}
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c-1.1 0-2 .9-2 2 0 .74.4 1.39 1 1.73V17h2v-2.27c.6-.34 1-.99 1-1.73 0-1.1-.9-2-2-2zm6-3h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                                            </svg>
                                        ) : (
                                            <svg
                                                className="w-4 h-4 transition-transform duration-200"
                                                style={{
                                                    color: 'var(--text3)',
                                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                }}
                                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* Accumulated reaction cloud — permanent stack, varied sizes, no fade */}
                                {contestantReactions.length > 0 && !isExpanded && (
                                    <div className="pointer-events-none absolute left-0 right-0 top-0 overflow-hidden" style={{ height: '56px' }} aria-hidden="true">
                                        {contestantReactions.map((r, i) => {
                                            const ts = r.timestamp?.toDate?.()?.getTime?.() || (r.timestamp?.toMillis?.()) || Date.now();
                                            // hash combining id and ts more chaotically — avoids modular collisions when ids/ts are sequential
                                            const idStr = String(r.id || i);
                                            let h = (ts | 0);
                                            for (let k = 0; k < idStr.length; k++) {
                                                h = ((h * 31) ^ idStr.charCodeAt(k)) | 0;
                                            }
                                            h = ((h ^ (h >>> 13)) * 0x5bd1e995) | 0;
                                            h = (h ^ (h >>> 15)) | 0;
                                            const seed = Math.abs(h);
                                            const leftPct = 4 + ((seed) % 90);
                                            const topPct = 6 + (((seed >>> 7)) % 80);
                                            const rot = (((seed >>> 11)) % 50) - 25;
                                            const sizeRoll = ((seed >>> 17)) % 100;
                                            // Sizes ~35% smaller than before: Hero ~7%: 32-46, Big ~18%: 18-28, Mid ~35%: 11-16, Small ~40%: 7-10
                                            let size;
                                            if (sizeRoll < 7) size = 32 + (sizeRoll % 14);
                                            else if (sizeRoll < 25) size = 18 + (sizeRoll % 10);
                                            else if (sizeRoll < 60) size = 11 + (sizeRoll % 5);
                                            else size = 7 + (sizeRoll % 3);
                                            const opacity = (0.55 + (((seed >>> 23) % 30) / 100)) * 0.6; // 40% more transparent
                                            return (
                                                <span
                                                    key={r.id || i}
                                                    className="absolute"
                                                    style={{
                                                        left: `${leftPct}%`,
                                                        top: `${topPct}%`,
                                                        fontSize: `${size}px`,
                                                        lineHeight: 1,
                                                        opacity,
                                                        transform: `rotate(${rot}deg)`,
                                                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                                        zIndex: 1 + ((seed >>> 29) % 5),
                                                    }}
                                                >
                                                    {r.emoji}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Voting panel */}
                                {isExpanded && (
                                    <div className="panel-expand" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                                        <div className="p-4">
                                            <div className="text-[10px] mb-4 flex items-center gap-1.5" style={{ color: isSaving ? 'var(--gold)' : 'var(--text3)' }}>
                                                {isSaving ? (
                                                    <>
                                                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 9a8 8 0 0114-5M20 15a8 8 0 01-14 5" />
                                                        </svg>
                                                        Saving…
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Saves as you tap
                                                    </>
                                                )}
                                            </div>

                                            <div className="space-y-5">
                                                {CRITERIA.map(criterion => {
                                                    const score = myScores[contestant.id]?.[criterion.id];
                                                    return (
                                                        <div key={criterion.id}>
                                                            <div className="flex justify-between items-baseline mb-2.5">
                                                                <span className="text-xs font-medium" style={{ color: 'var(--text2)' }}>{criterion.label}</span>
                                                                <span className="text-[10px]" style={{ color: 'var(--text3)' }}>{(criterion.weight * 100).toFixed(0)}%</span>
                                                            </div>
                                                            <div className="grid grid-cols-10 gap-1">
                                                                {[1,2,3,4,5,6,7,8,9,10].map(v => (
                                                                    <button
                                                                        key={v}
                                                                        onClick={(e) => { e.stopPropagation(); handleScoreSelect(contestant.id, criterion.id, v); }}
                                                                        className="aspect-square text-xs font-bold transition-all active:scale-90"
                                                                        style={{
                                                                            borderRadius: 0,
                                                                            minHeight: 36,
                                                                            fontFamily: '"JetBrains Mono", monospace',
                                                                            fontWeight: 600,
                                                                            background: score === v
                                                                                ? 'linear-gradient(135deg, #ff2d87, #7c3aed)'
                                                                                : 'var(--surface-hover)',
                                                                            color: score === v ? '#fff' : 'var(--text3)',
                                                                        }}
                                                                    >
                                                                        {v}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {myScores[contestant.id]?.overall !== undefined && (
                                                    <div className="flex justify-center pt-1">
                                                        <div className="text-center">
                                                            <div
                                                                className="text-[10px] uppercase tracking-wider mb-0.5"
                                                                style={{ color: 'var(--text3)', letterSpacing: '2px' }}
                                                            >
                                                                OVERALL
                                                            </div>
                                                            <div
                                                                className="font-mono text-2xl font-bold tabular-nums"
                                                                style={{
                                                                    fontFamily: '"JetBrains Mono", monospace',
                                                                    fontWeight: 700,
                                                                    color: scoreColor(myScores[contestant.id].overall),
                                                                }}
                                                            >
                                                                {myScores[contestant.id].overall.toFixed(2)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

        </div>
    );
};

export default Voting;
